import React, { createContext, useContext, useState, useEffect } from 'react';
import { Branch } from '../types/branch';
import { branchService } from '../services/branch.service';

interface BranchContextType {
  selectedBranch: Branch | null;
  branches: Branch[];
  isLoading: boolean;
  selectBranch: (branch: Branch) => void;
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

export const BranchProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const data = await branchService.getPublicBranches();
        setBranches(data);

        // Check if there's a saved branch in localStorage
        const savedBranchId = localStorage.getItem('fruitshop_selected_branch');
        if (savedBranchId && data.length > 0) {
          const savedBranch = data.find(
            (branch) => branch._id === savedBranchId
          );
          if (savedBranch) {
            setSelectedBranch(savedBranch);
          } else {
            // If saved branch is not found, use the first one
            setSelectedBranch(data[0]);
            localStorage.setItem('fruitshop_selected_branch', data[0]._id);
          }
        } else if (data.length > 0) {
          // If no saved branch, use the first one
          setSelectedBranch(data[0]);
          localStorage.setItem('fruitshop_selected_branch', data[0]._id);
        }
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBranches();
  }, []);

  const selectBranch = (branch: Branch) => {
    setSelectedBranch(branch);
    localStorage.setItem('fruitshop_selected_branch', branch._id);
  };

  return (
    <BranchContext.Provider
      value={{
        selectedBranch,
        branches,
        isLoading,
        selectBranch,
      }}
    >
      {children}
    </BranchContext.Provider>
  );
};

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (context === undefined) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};
